import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/i18n/index";
import ProfessionalLayout from "@/components/layout/ProfessionalLayout";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { formatCancerDisplay } from "@/components/shared/OncologyContext";
import api from "@/api";
import { toast } from "sonner";
import {
  AlertTriangle, Users, CheckCircle, Clock, ChevronRight,
  RefreshCw, ArrowUpRight, X, Hospital, Eye, Heart, Building2, Calendar, Zap, ClipboardCheck, Pill
} from "lucide-react";
import { getItems } from "@/utils/apiHelpers";
import PreChemoBilanEncart from "@/components/clinical/PreChemoBilanEncart";
import MedicalDecisionTaskModal from "@/components/shared/MedicalDecisionTaskModal";
import { DECISION_SOURCES } from "@/lib/medicalDecisionTemplates";
import ProfessionalTodayActions from "@/components/professional/ProfessionalTodayActions";

export default function DoctorDashboard() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [escalatedAlerts, setEscalatedAlerts] = useState([]);
  // Phase point 4 — RDV du jour (patients dont je suis référent)
  const [todayAppts, setTodayAppts] = useState([]);
  const [oralPatients, setOralPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [showResolveModal, setShowResolveModal] = useState(false);
  
  // Resolution modal state
  const [resolveOutcome, setResolveOutcome] = useState("");
  const [resolveNotes, setResolveNotes] = useState("");
  const [resolveLoading, setResolveLoading] = useState(false);

  // Quick Action Launcher (Prescrire / déclencher une action)

  const fetchData = async () => {
    try {
      setLoading(true);
      const [escalatedRes, todayRes, oralRes] = await Promise.all([
        api.get("/alerts/escalated").catch(() => ({ data: { items: [] } })),
        api.get("/appointments/doctor/today").catch(() => ({ data: [] })),
        api.get("/oral-adherence/ide/patients").catch(() => ({ data: { items: [] } })),
      ]);

      setEscalatedAlerts(getItems(escalatedRes.data));
      setTodayAppts(Array.isArray(todayRes.data) ? todayRes.data : getItems(todayRes.data));
      setOralPatients(getItems(oralRes.data).slice(0, 4));

      // Mark as seen
      api.post("/kpi/notifications/mark-seen?notification_type=escalated").catch(() => {});
    } catch (err) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openResolveModal = (alert) => {
    setSelectedAlert(alert);
    setResolveOutcome("");
    setResolveNotes("");
    setShowResolveModal(true);
  };

  const handleSubmitResolve = async () => {
    if (!resolveOutcome) {
      toast.error("Veuillez sélectionner l'issue de l'alerte");
      return;
    }

    setResolveLoading(true);
    try {
      await api.patch(`/alerts/${selectedAlert.id}/resolve`, {
        resolutionOutcome: resolveOutcome,
        notes: resolveNotes,
        validatedBy: "doctor",
      });
      toast.success("Alerte résolue");
      setShowResolveModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la résolution");
    } finally {
      setResolveLoading(false);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleString("fr-FR", {
      day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit"
    });
  };

  // Patient list component for each category
  const PatientCategorySection = ({ 
    title, 
    icon: Icon, 
    patients, 
    bgColor, 
    iconColor, 
    borderColor,
    emptyMessage,
    showLastCheckin = false
  }) => (
    <div className={`dash-card border-l-4 ${borderColor}`}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-slate-800 flex items-center gap-3">
          <div className={`p-2 ${bgColor} rounded-xl`}>
            <Icon className={`w-5 h-5 ${iconColor}`} strokeWidth={2} />
          </div>
          {title}
          {patients.length > 0 && (
            <span className={`${bgColor} ${iconColor} text-xs px-2 py-0.5 rounded-full font-medium`}>
              {patients.length}
            </span>
          )}
        </h2>
      </div>

      {patients.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-6">{emptyMessage}</p>
      ) : (
        <div className="space-y-2">
          {patients.slice(0, 5).map((p) => (
            <Link
              key={p.id}
              to={`/doctor/patients/${p.id}`}
              className={`flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-all group`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl ${bgColor} flex items-center justify-center ${iconColor} font-semibold text-sm`}>
                  {p.firstName?.[0]}{p.lastName?.[0]}
                </div>
                <div>
                  <p className="font-medium text-slate-800 text-sm">{p.firstName} {p.lastName}</p>
                  <p className="text-xs text-slate-500 truncate max-w-[150px]">{formatCancerDisplay(p)}</p>
                  {showLastCheckin && (
                    <p className="text-xs text-amber-600 mt-0.5">
                      {p.lastCheckinAt 
                        ? `Dernier: ${new Date(p.lastCheckinAt).toLocaleDateString("fr-FR")}`
                        : "Jamais de check-in"
                      }
                    </p>
                  )}
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 group-hover:translate-x-1 transition-all" />
            </Link>
          ))}
          {patients.length > 5 && (
            <Link to="/doctor/patients" className="block text-center text-sm text-primary hover:text-teal-700 py-2">
              Voir les {patients.length - 5} autres →
            </Link>
          )}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <ProfessionalLayout>
        <div className="space-y-6">
          <div className="h-12 bg-slate-200 rounded-xl animate-pulse w-1/3" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="dash-card h-48 animate-pulse bg-slate-100" />
            ))}
          </div>
        </div>
      </ProfessionalLayout>
    );
  }

  const doctorTodayActions = buildDoctorTodayActions({
    escalatedAlerts,
    oralPatients,
    todayAppts,
  });

  return (
    <ProfessionalLayout>
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Dr. {user?.lastName}
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              {new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
            </p>
          </div>
          <div className="grid grid-cols-2 sm:flex sm:items-center gap-2 sm:gap-3">
            <button
              onClick={fetchData}
              data-testid="refresh-dashboard"
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-3 sm:py-2.5 text-sm text-slate-600 hover:text-slate-800 bg-white rounded-xl border border-slate-200/60 font-medium shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 min-h-[46px]"
            >
              <RefreshCw className="w-4 h-4" strokeWidth={1.5} />
              Actualiser
            </button>
          </div>
        </div>

        <ProfessionalTodayActions
          title="À traiter aujourd’hui"
          subtitle="Décisions médicales, escalades IDE, observance et rendez-vous"
          actions={doctorTodayActions}
          emptyLabel="Aucune action médicale prioritaire pour le moment"
          testId="doctor-today-actions-panel"
        />

        <div className="grid grid-cols-3 gap-2 sm:hidden" data-testid="doctor-mobile-priority-strip">
          <MobilePriorityTile icon={ArrowUpRight} label="Escalades" value={escalatedAlerts.length} tone="blue" />
          <MobilePriorityTile icon={ClipboardCheck} label="Chimio" value="À valider" tone="violet" />
          <MobilePriorityTile icon={Calendar} label="RDV" value={todayAppts.length} tone="emerald" />
        </div>

        {/* Sprint 6 — Bilans pré-chimio à valider */}
        <PreChemoBilanEncart mode="doctor" />

        <div className="dash-card border-l-4 border-amber-400" data-testid="doctor-oral-adherence-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-slate-800 flex items-center gap-3">
              <div className="p-2 bg-amber-50 rounded-xl">
                <Pill className="w-5 h-5 text-amber-600" strokeWidth={2} />
              </div>
              Observance per os
              {oralPatients.length > 0 && (
                <span className="bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full font-medium" data-testid="doctor-oral-adherence-count">
                  {oralPatients.length}
                </span>
              )}
            </h2>
            <Link to="/doctor/oral-adherence" className="text-xs font-bold text-amber-700 hover:text-amber-900" data-testid="doctor-oral-adherence-open-page">
              Ouvrir
            </Link>
          </div>
          {oralPatients.length === 0 ? (
            <div className="text-center py-6 text-slate-400" data-testid="doctor-oral-adherence-empty">
              <Pill className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p className="text-sm font-medium">Aucun patient assigné sous traitement per os actif</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="doctor-oral-adherence-list">
              {oralPatients.map((patient) => (
                <Link
                  key={patient.patientId}
                  to="/doctor/oral-adherence"
                  data-testid={`doctor-oral-adherence-row-${patient.patientId}`}
                  className="flex items-center justify-between p-3 rounded-xl border border-slate-200 bg-white hover:bg-amber-50 transition-colors"
                >
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-800 text-sm truncate" data-testid={`doctor-oral-adherence-name-${patient.patientId}`}>{patient.patientName}</p>
                    <p className="text-xs text-slate-500 truncate" data-testid={`doctor-oral-adherence-details-${patient.patientId}`}>
                      {(patient.treatments || []).map(t => t.molecule).filter(Boolean).join(" · ") || "Traitement per os"} · {patient.adherenceRate7d}% sur 7 j
                    </p>
                  </div>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full border font-bold ${patient.needsAttention ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`} data-testid={`doctor-oral-adherence-risk-${patient.patientId}`}>
                    {patient.needsAttention ? "À prioriser" : "À jour"}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Escalated Alerts Section - Only shows alerts escalated by nurses */}
        <div className="dash-card border-l-4 border-blue-400">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-slate-800 flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-xl">
                <ArrowUpRight className="w-5 h-5 text-blue-500" strokeWidth={2} />
              </div>
              Alertes escaladées
              {escalatedAlerts.length > 0 && (
                <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full font-medium">
                  {escalatedAlerts.length}
                </span>
              )}
            </h2>
          </div>

          {escalatedAlerts.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <CheckCircle className="w-10 h-10 mx-auto mb-2 text-emerald-400" />
              <p className="text-sm font-medium">Aucune alerte escaladée par l'IDE</p>
            </div>
          ) : (
            <div className="space-y-2">
              {escalatedAlerts.slice(0, 6).map((alert) => (
                <div
                  key={alert.id}
                  data-testid={`escalated-alert-${alert.id}`}
                  className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-xl border ${
                    alert.level === "CRITICAL"
                      ? "bg-rose-50 border-rose-200"
                      : "bg-blue-50 border-blue-200"
                  }`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <StatusBadge status={alert.level === "CRITICAL" ? "CRITICAL" : "TO_MONITOR"} size="sm" />
                      <p className="font-medium text-slate-800">{alert.patientName}</p>
                      <span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
                        Par {alert.escalatorName || "IDE"}
                      </span>
                    </div>
                    {alert.escalationTransmission && (
                      <p className="text-xs text-slate-600 mt-1 italic">
                        "{alert.escalationTransmission.slice(0, 100)}..."
                      </p>
                    )}
                    <p className="text-xs text-slate-400 mt-1 flex items-center gap-2">
                      {formatDateTime(alert.escalatedAt || alert.createdAt)}
                      {hoursSince(alert.escalatedAt || alert.createdAt) >= 2 && (
                        <span className="text-[11px] font-semibold text-amber-800 bg-amber-100 px-2 py-0.5 rounded-full" data-testid={`escalation-stale-${alert.id}`}>
                          non repris depuis {hoursSince(alert.escalatedAt || alert.createdAt)} h
                        </span>
                      )}
                    </p>
                  </div>
                  <button
                    className="w-full sm:w-auto text-sm bg-emerald-600 text-white px-4 py-3 sm:py-2 rounded-lg hover:bg-emerald-700 font-medium min-h-[44px]"
                    onClick={() => openResolveModal(alert)}
                    data-testid={`resolve-alert-${alert.id}`}
                  >
                    Résoudre
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Phase point 4 — RDV du jour de mes patients */}
        <div className="dash-card border-l-4 border-emerald-400" data-testid="doctor-today-appts">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-slate-800 flex items-center gap-3">
              <div className="p-2 bg-emerald-50 rounded-xl">
                <Clock className="w-5 h-5 text-emerald-600" strokeWidth={2} />
              </div>
              RDV du jour
              {todayAppts.length > 0 && (
                <span className="bg-emerald-100 text-emerald-700 text-xs px-2 py-0.5 rounded-full font-medium">
                  {todayAppts.length}
                </span>
              )}
            </h2>
          </div>

          {todayAppts.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Calendar className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p className="text-sm font-medium">Aucun RDV programmé aujourd'hui pour vos patients</p>
            </div>
          ) : (
            <div className="space-y-2">
              {todayAppts.map((a) => {
                const time = a.date ? new Date(a.date).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "—";
                const typeLabel = {
                  consultation: "Consultation",
                  imagerie: "Imagerie",
                  analyses: "Analyses",
                  chimio_hdj: "Chimio HDJ",
                  radiotherapie: "Radiothérapie",
                }[a.type] || a.type || "RDV";
                return (
                  <Link
                    key={a.id}
                    to={`/doctor/patients/${a.patientId}`}
                    data-testid={`today-appt-${a.id}`}
                    className="flex items-center justify-between p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors border border-transparent hover:border-slate-200 min-h-[58px]"
                  >
                    <div className="flex items-center gap-3">
                      <div className="text-sm font-bold text-emerald-700 w-14">{time}</div>
                      <div>
                        <p className="font-medium text-slate-800 text-sm">{a.patientName}</p>
                        <p className="text-xs text-slate-500">{typeLabel}</p>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300" />
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* Quick link to all patients */}
        <div className="text-center">
          <Link
            to="/doctor/patients"
            className="inline-flex items-center gap-2 text-sm text-primary hover:text-teal-700 font-medium"
          >
            <Users className="w-4 h-4" />
            Voir tous les patients →
          </Link>
        </div>
      </div>

      {/* Resolve Modal */}
      {showResolveModal && selectedAlert && (
        <ResolveModal
          alert={selectedAlert}
          onClose={() => {
            setShowResolveModal(false);
            setSelectedAlert(null);
          }}
          onResolved={() => {
            setShowResolveModal(false);
            setSelectedAlert(null);
            fetchData();
          }}
        />
      )}

    </ProfessionalLayout>
  );
}

function MobilePriorityTile({ icon: Icon, label, value, tone }) {
  const colors = {
    blue: "bg-blue-50 text-blue-700 border-blue-100",
    violet: "bg-violet-50 text-violet-700 border-violet-100",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
  };
  return (
    <div className={`rounded-xl border p-3 ${colors[tone] || colors.emerald}`} data-testid={`doctor-mobile-priority-${label.toLowerCase()}`}>
      <Icon className="w-4 h-4 mb-1" />
      <p className="text-[11px] font-semibold leading-tight">{label}</p>
      <p className="text-sm font-black leading-tight mt-0.5">{value}</p>
    </div>
  );
}

function buildDoctorTodayActions({ escalatedAlerts, oralPatients, todayAppts }) {
  const actions = [];
  const oralNeedsAttention = (oralPatients || []).filter(p => p.needsAttention);

  if ((escalatedAlerts || []).length > 0) {
    const first = escalatedAlerts[0];
    actions.push({
      id: "escalated-alerts",
      icon: ArrowUpRight,
      tone: first.level === "CRITICAL" ? "rose" : "blue",
      label: "Traiter les escalades IDE",
      detail: `${first.patientName || "Patient"} · décision médicale attendue`,
      count: escalatedAlerts.length,
      to: first.patientId ? `/doctor/patients/${first.patientId}` : "/doctor/patients",
    });
  }

  if (oralNeedsAttention.length > 0) {
    actions.push({
      id: "oral-adherence",
      icon: Pill,
      tone: "amber",
      label: "Revoir l’observance per os",
      detail: `${oralNeedsAttention[0].patientName || "Patient"} · observance à prioriser`,
      count: oralNeedsAttention.length,
      to: "/doctor/oral-adherence",
    });
  }

  if ((todayAppts || []).length > 0) {
    actions.push({
      id: "appointments",
      icon: Calendar,
      tone: "emerald",
      label: "Préparer les rendez-vous du jour",
      detail: `${todayAppts[0].patientName || "Patient"} · prochain RDV`,
      count: todayAppts.length,
      to: todayAppts[0].patientId ? `/doctor/patients/${todayAppts[0].patientId}` : "/doctor/patients",
    });
  }

  return actions.slice(0, 5);
}

const hoursSince = (iso) => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 3600000) : 0);

// Resolve Modal Component
function ResolveModal({ alert, onClose, onResolved }) {
  const [resolveOutcome, setResolveOutcome] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [decisionOpen, setDecisionOpen] = useState(false);

  const handleResolve = async () => {
    if (!resolveOutcome) {
      toast.error("Veuillez sélectionner l'issue de l'alerte");
      return;
    }

    setLoading(true);
    try {
      await api.patch(`/alerts/${alert.id}/resolve`, {
        resolutionOutcome: resolveOutcome,
        notes: notes || null
      });
      toast.success("Alerte résolue avec succès");
      // Phase 2 : si hospitalisation nécessaire, proposer une mission SEC
      if (resolveOutcome === "hospitalisation_necessaire") {
        setDecisionOpen(true);
      } else {
        onResolved();
      }
    } catch (err) {
      toast.error("Erreur lors de la résolution");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b bg-emerald-50">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <StatusBadge status={alert.level === "CRITICAL" ? "CRITICAL" : "TO_MONITOR"} />
                <h2 className="text-xl font-bold text-slate-800">Résoudre l'alerte</h2>
              </div>
              <p className="text-slate-600">
                Patient: <span className="font-semibold">{alert.patientName}</span>
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/50 rounded-lg">
              <X className="w-6 h-6 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {alert.escalationTransmission && (
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
              <p className="text-xs font-medium text-blue-700 mb-2">Transmission de l'IDE :</p>
              <p className="text-sm text-slate-700">{alert.escalationTransmission}</p>
              {alert.escalatorName && (
                <p className="text-xs text-blue-600 mt-2">Par: {alert.escalatorName}</p>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-3">Issue de cette alerte *</label>
            <div className="space-y-2">
              {[
                { value: "hospitalisation_necessaire", label: "Hospitalisation nécessaire", desc: "Le patient a dû être hospitalisé", icon: Hospital, borderClass: "border-red-500", bgClass: "bg-red-50", iconBgClass: "bg-red-100", iconClass: "text-red-600" },
                { value: "hospitalisation_evitee", label: "Hospitalisation évitée", desc: "Situation gérée en ambulatoire", icon: CheckCircle, borderClass: "border-green-500", bgClass: "bg-green-50", iconBgClass: "bg-green-100", iconClass: "text-green-600" },
                { value: "simple_surveillance", label: "Simple surveillance", desc: "Alerte mineure, surveillance à domicile", icon: Eye, borderClass: "border-sky-500", bgClass: "bg-sky-50", iconBgClass: "bg-sky-100", iconClass: "text-sky-600" },
                { value: "erreur_declaration", label: "Erreur de déclaration", desc: "Fausse alerte vérifiée avec le patient — justification obligatoire", icon: AlertTriangle, borderClass: "border-slate-500", bgClass: "bg-slate-50", iconBgClass: "bg-slate-100", iconClass: "text-slate-600" },
              ].map(opt => {
                const Icon = opt.icon;
                const isSelected = resolveOutcome === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => setResolveOutcome(opt.value)}
                    className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                      isSelected ? `${opt.borderClass} ${opt.bgClass}` : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <div className={`p-2 rounded-lg ${isSelected ? opt.iconBgClass : "bg-slate-100"}`}>
                      <Icon className={`w-5 h-5 ${opt.iconClass}`} />
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-slate-800">{opt.label}</p>
                      <p className="text-xs text-slate-500">{opt.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Commentaire médical (facultatif):</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ajouter un commentaire..."
              className="w-full p-4 border border-slate-200 rounded-xl resize-none h-24 focus:ring-2 focus:ring-blue-200 focus:border-blue-500 outline-none"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="p-6 border-t bg-slate-50 flex gap-3 justify-end">
          <button onClick={onClose} disabled={loading} className="px-6 py-3 text-slate-600 hover:bg-slate-200 rounded-xl">
            Annuler
          </button>
          <button
            onClick={handleResolve}
            disabled={loading || !resolveOutcome || (resolveOutcome === "erreur_declaration" && (notes || "").trim().length < 10)}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium flex items-center gap-2 disabled:opacity-50"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                Résoudre l'alerte
              </>
            )}
          </button>
        </div>
      </div>
      <MedicalDecisionTaskModal
        open={decisionOpen}
        onClose={() => { setDecisionOpen(false); onResolved(); }}
        onDone={() => { setDecisionOpen(false); onResolved(); }}
        decisionSource={DECISION_SOURCES.ALERT_RESOLVE_HOSPITALIZATION}
        patientId={alert.patientId}
        patientName={alert.patientName}
        context={{ reason: alert.reasons?.join(", ") || alert.escalationTransmission }}
        sourceRefs={{ alertId: alert.id }}
      />
    </div>
  );
}
